import { useCallback } from 'react';
import { fetchWithAuth } from '../../actor/api/Services/shared/utils/fetchWithAuth';
import { makeApiUrl, useAddOrderFn } from '../../aws';
import { Order, OrderDto, Side } from '../../aws/utils/dynamoDb/orders/type';

const makeSendOrder = (props: {
  sAccId: number | null | undefined,
  tAccId?: string | null,
  side: Side,
  initialContent: string,
  addOrder: (element: OrderDto) => Promise<Order[] | undefined>,
  closeModal: () => void,
  setOrderForbiddenError: React.Dispatch<React.SetStateAction<boolean>>,
  setHasError: React.Dispatch<React.SetStateAction<boolean>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
}): React.FormEventHandler<HTMLFormElement> => async (e) => {
  e.preventDefault();
  const {
    sAccId,
    tAccId,
    side,
    initialContent,
    addOrder,
    closeModal,
    setOrderForbiddenError,
    setHasError,
    setLoading,
  } = props;
  try {
    if (sAccId == null) {
      throw new Error('no sAccId provided');
    }
    if (side !== Side.BUY && tAccId == null) {
      throw new Error('should have a tAccId');
    }
    setLoading(true);
    const {
      value,
    } = e.target[0];
    const message = value === initialContent ? undefined : value;
    const parsedSAccId = tAccId != null ? parseInt(tAccId, 10) : null;
    const body = JSON.stringify({
      'S_ACC.ID': sAccId,
      side,
      message,
      ...(parsedSAccId != null ? { 'T_ACC.ID': parsedSAccId } : {}),
    });
    await fetchWithAuth(makeApiUrl('order'), {
      method: 'POST',
      body,
    });
    await addOrder({
      side,
      sAccId,
      ...(parsedSAccId != null ? { tAccId: parsedSAccId } : {}),
      message,
    });
    setLoading(false);
    closeModal();
  } catch (err) {
    if (err && typeof err === 'object' && 'status' in err && err.status === 403) {
      setOrderForbiddenError(true);
      setLoading(false);
    } else {
      if (window.awsRum) {
        window.awsRum.recordError(err);
      }
      if (setHasError) {
        setHasError(true);
        setLoading(false);
        closeModal();
      }
    }
  }
};

export const useMakeSendOrder = (props: {
  sAccId: number | null | undefined,
  tAccId?: string | null,
  side: Side,
  initialContent: string,
  closeModal: () => void,
  setHasError: React.Dispatch<React.SetStateAction<boolean>>,
  setOrderForbiddenError: React.Dispatch<React.SetStateAction<boolean>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
}) => {
  const {
    sAccId,
    tAccId,
    side,
    initialContent,
    closeModal,
    setOrderForbiddenError,
    setHasError,
    setLoading,
  } = props;
  const addOrder = useAddOrderFn();
  const onSubmit = useCallback(makeSendOrder({
    sAccId,
    tAccId,
    side,
    initialContent,
    addOrder,
    closeModal,
    setOrderForbiddenError,
    setHasError,
    setLoading,
  }), [
    sAccId,
    side,
    initialContent,
    addOrder,
    closeModal,
    setOrderForbiddenError,
    setHasError,
    setLoading,
  ]);
  return onSubmit;
};
